const W = 1080;
const H = 1350;
const ELECTRIC = "#2170E8";

// Marks which words of `text` fall inside the highlighted phrase.
export function highlightMask(text, phrase) {
  const words = text.split(" ");
  const p = (phrase || "").toLowerCase().trim();
  const idx = p ? text.toLowerCase().indexOf(p) : -1;
  let pos = 0;
  return words.map((w) => {
    const start = pos;
    const end = pos + w.length;
    pos = end + 1;
    return idx >= 0 && start < idx + p.length && end > idx;
  });
}

const loadImg = (src) =>
  new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

function drawCover(ctx, img) {
  const r = Math.max(W / img.width, H / img.height);
  const iw = img.width * r;
  const ih = img.height * r;
  ctx.drawImage(img, (W - iw) / 2, (H - ih) / 2, iw, ih);
}

function alphaBox(img) {
  const c = document.createElement("canvas");
  c.width = img.width;
  c.height = img.height;
  const ctx = c.getContext("2d");
  ctx.drawImage(img, 0, 0);
  const { data } = ctx.getImageData(0, 0, c.width, c.height);
  let x0 = c.width, y0 = c.height, x1 = 0, y1 = 0;
  for (let y = 0; y < c.height; y++) {
    for (let x = 0; x < c.width; x++) {
      if (data[(y * c.width + x) * 4 + 3] > 10) {
        x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y);
      }
    }
  }
  return x1 > x0 ? { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 } : { x: 0, y: 0, w: img.width, h: img.height };
}

function spacedCentered(ctx, str, y, spacing) {
  const chars = [...str];
  const total = chars.reduce((s, ch) => s + ctx.measureText(ch).width, 0) + spacing * (chars.length - 1);
  let x = (W - total) / 2;
  ctx.textAlign = "left";
  chars.forEach((ch) => {
    ctx.fillText(ch, x, y);
    x += ctx.measureText(ch).width + spacing;
  });
}

function wrap(ctx, words, maxW) {
  const lines = [];
  let cur = [];
  words.forEach((w, i) => {
    const test = [...cur.map((j) => words[j]), w].join(" ");
    if (cur.length && ctx.measureText(test).width > maxW) {
      lines.push(cur);
      cur = [i];
    } else cur.push(i);
  });
  if (cur.length) lines.push(cur);
  return lines;
}

function drawVerse(ctx, text, highlight) {
  const words = text.toUpperCase().split(" ");
  const mask = highlightMask(text, highlight);
  let size = 104;
  let lines;
  for (; size >= 48; size -= 4) {
    ctx.font = `${size}px "Anton", Impact, sans-serif`;
    lines = wrap(ctx, words, W - 170);
    if (lines.length * size * 1.08 <= 700) break;
  }
  const lh = size * 1.08;
  const blockH = lines.length * lh;
  const top = 340 + (740 - blockH) / 2;
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  const space = ctx.measureText(" ").width;
  lines.forEach((ln, li) => {
    const widths = ln.map((i) => ctx.measureText(words[i]).width);
    let x = (W - (widths.reduce((a, b) => a + b, 0) + space * (ln.length - 1))) / 2;
    ln.forEach((wi, k) => {
      ctx.shadowColor = mask[wi] ? "rgba(33,112,232,0.9)" : "transparent";
      ctx.shadowBlur = mask[wi] ? 34 : 0;
      ctx.fillStyle = mask[wi] ? ELECTRIC : "#FFFFFF";
      ctx.fillText(words[wi], x, top + li * lh);
      x += widths[k] + space;
    });
  });
  ctx.shadowBlur = 0;
  ctx.textBaseline = "alphabetic";
  return top + blockH;
}

// Builds a 1080x1350 branded verse PNG in the browser.
export async function generateVerseImage({ text, reference, highlight, eyebrow, footer, logo, bgImage }) {
  await Promise.all([
    document.fonts.load('100px "Anton"'),
    document.fonts.load('600 30px "Poppins"'),
    document.fonts.load('500 22px "Poppins"'),
  ]).catch(() => {});
  const [logoImg, bgImg] = await Promise.all([loadImg(logo), loadImg(bgImage)]);

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#050505";
  ctx.fillRect(0, 0, W, H);
  if (bgImg) {
    ctx.globalAlpha = 0.3;
    drawCover(ctx, bgImg);
    ctx.globalAlpha = 1;
  }
  const lg = ctx.createLinearGradient(0, 0, 0, H);
  lg.addColorStop(0, "rgba(5,5,5,0.88)");
  lg.addColorStop(0.5, "rgba(5,5,5,0.5)");
  lg.addColorStop(1, "rgba(5,5,5,0.94)");
  ctx.fillStyle = lg;
  ctx.fillRect(0, 0, W, H);
  const rg = ctx.createRadialGradient(W / 2, H * 0.52, 0, W / 2, H * 0.52, W * 0.7);
  rg.addColorStop(0, "rgba(33,112,232,0.3)");
  rg.addColorStop(1, "rgba(33,112,232,0)");
  ctx.fillStyle = rg;
  ctx.fillRect(0, 0, W, H);

  if (logoImg) {
    const b = alphaBox(logoImg);
    const lh = 104;
    const lw = (b.w * lh) / b.h;
    ctx.drawImage(logoImg, b.x, b.y, b.w, b.h, (W - lw) / 2, 92, lw, lh);
  }

  ctx.fillStyle = ELECTRIC;
  ctx.font = '600 26px "Poppins", sans-serif';
  spacedCentered(ctx, eyebrow.toUpperCase(), 292, 7);

  const verseBottom = drawVerse(ctx, text, highlight);

  ctx.fillStyle = ELECTRIC;
  ctx.font = '600 32px "Poppins", sans-serif';
  spacedCentered(ctx, reference.toUpperCase(), verseBottom + 84, 6);

  ctx.fillStyle = "rgba(255,255,255,0.2)";
  ctx.fillRect(W / 2 - 60, H - 132, 120, 2);
  ctx.fillStyle = "rgba(237,237,237,0.62)";
  ctx.font = '500 22px "Poppins", sans-serif';
  spacedCentered(ctx, footer.toUpperCase(), H - 82, 5);

  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}
